/**
 * Normaliza geometrías GeoJSON con coordenadas malformadas.
 *
 * Algunos servidores WFS (p. ej. ArcGIS Server de SERNANP) declaran un tipo
 * de geometría (MultiPolygon) pero emiten las coordenadas con un nivel de
 * anidamiento incorrecto. Esto causa errores en parseadores estrictos
 * (ArcGIS WFSLayer, GeoJSONLayer, etc.).
 *
 * Patrones soportados:
 *
 * | Tipo declarado | Coordinates recibidas              | Reparación                    |
 * |----------------|-------------------------------------|-------------------------------|
 * | MultiPolygon   | `[[x,y], ...]` (par nivel)         | `[[[[x,y], ...]]]`            |
 * | MultiPolygon   | `[[[x,y], ...], ...]` (anillo nivel)| `[[[[x,y], ...]]]`            |
 *
 * Funciona **in-place** sobre el FeatureCollection.
 *
 * @example
 * const geojson = await response.json();
 * GeoJsonNormalizerUtil.normalize(geojson);
 */
export class GeoJsonNormalizerUtil {

  /**
   * Normaliza las coordenadas de todas las geometrías del FeatureCollection.
   * Mutación in-place: no devuelve un nuevo objeto.
   */
  static normalize(geojson: any): void {
    if (!geojson || geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) return;
    for (const feature of geojson.features) {
      GeoJsonNormalizerUtil.normalizeGeometry(feature?.geometry);
    }
  }

  private static normalizeGeometry(geometry: any): void {
    if (!geometry || geometry.type !== 'MultiPolygon' || !Array.isArray(geometry.coordinates)) return;
    if (geometry.coordinates.length === 0) return;

    const first = geometry.coordinates[0];
    if (!Array.isArray(first)) return;

    // Patrón 1: first = [x, y] — par de coordenadas a nivel superficial.
    // MultiPolygon válido requiere: [[[x,y], ...]]
    if (GeoJsonNormalizerUtil.isCoordinatePair(first)) {
      geometry.coordinates = [[geometry.coordinates]];
      return;
    }

    // Patrón 2: first = [[x,y], ...] — anillo de polígono sin envolver.
    if (GeoJsonNormalizerUtil.isRing(first)) {
      geometry.coordinates = geometry.coordinates.map((ring: any) => [ring]);
    }
  }

  private static isCoordinatePair(value: any): boolean {
    return Array.isArray(value) && value.length >= 2
      && typeof value[0] === 'number' && typeof value[1] === 'number';
  }

  private static isRing(value: any): boolean {
    return Array.isArray(value) && value.length > 0 && GeoJsonNormalizerUtil.isCoordinatePair(value[0]);
  }
}
